import { alignPixel, formatSize, getCorrectDpi, isTouchDevice, parseGeometryStyleHash, poiInfoToJson } from "./utils";
import { IDefPosition, IPoiData, TWasmEventHoverType, IMapStyle, TLangs } from "./interfaces";
import { deferPromise } from "~/util/general";
import { ILonLatPosition, IWasmCustomData, IXYPosition, TWasmId, TWasmOverlay } from "~/interfaces";
import WasmMapMarker from "./entities/wasm-map-marker";
import WasmMapGeometry from "./entities/wasm-map-geometry";
import { IStoreItem, IWasmStoreGeometry, IWasmStoreMarker } from "./store/interfaces";
import { debounce } from "~/lib/debounce";
import WasmTextures from "./textures";
import { DEF_DPIS, LAYER_MAPPING, LAYER_MAPPING_NUMB, OPHOTO_YEAR_SRC_MAPPING, DEF_POSITION, TILT_DATA, CZECH_TILES, WASM_VERSION } from "./const";
import { registerTextures } from "./textures-set";
import { bindWasmTextureUrlProvider } from "./texture-url-provider";
import WasmMapMarkerSharedPosition from "./entities/wasm-map-marker-shared-position";
import { isWasmEnabled } from "./feature-detection";
import { createMapBestfit } from "~/ui/map/bestfit";
import WasmScroll from "./wasm-scroll";

export type TWasmMapClick = {
	longitude: number;
	latitude: number;
	wasmId: TWasmId;
	pois: Array<IPoiData>;
	ctrlKey: boolean;
	shiftKey: boolean;
	altKey: boolean;
	metaKey: boolean;
	button: number;
	pointerType: string;
	clientX: number;
	clientY: number;
	contactX: number;
	contactY: number;
	rasterLayer: string;
};

export type TWasmMapHover = {
	longitude: number;
	latitude: number;
	type: TWasmEventHoverType;
	wasmId: TWasmId;
	poi: IPoiData;
	clientX: number;
	clientY: number;
	contactX: number;
	contactY: number;
	rasterLayer: string;
	actionUserData?: IWasmCustomData;
};

export interface IWasmUrlReplacement {
	fromUrl: string;
	toUrl: string;
	authServerUrl?: string;
}

interface IScreenshot {
	data: string;
	width: number;
	height: number;
}

interface IActivePointerItem {
	event: PointerEvent;
	type: "down" | "tilt" | "fit" | "dragging";
}

type THoverData = Pick<TWasmMapHover, "longitude" | "latitude" | "clientX" | "clientY" | "contactX" | "contactY" | "actionUserData">;

interface IWasmOpts {
	parentElem: HTMLElement;
	lang?: TLangs;
	debug?: boolean;
	canvasQuery?: string;
	urlReplacement?: IWasmUrlReplacement;
	animTo?: number;
	moveTo?: number;
	dpi?: number;
	loadTo?: number;
	resizeTo?: number;
	longTapTo?: number;
	entityChangeTo?: number;
	maxFps?: number;
	urls?: {
		poiAdmin: string;
		styles: string;
	};
	position?: IDefPosition;
	mapStyle?: IMapStyle;
	release?: boolean;
	isMobile?: boolean;
	clickDistanceMouse?: number;
	clickDistanceTouch?: number;
	dblClickTo?: number;
	enableTilt?: boolean;
	onMapClick?: (data: TWasmMapClick) => void;
	onHover?: (data: TWasmMapHover) => void;
	onContextMenu?: (event: PointerEvent, poisData: Array<IPoiData>, wasmId: number) => void;
	onDblClick?: (event: PointerEvent) => void;
	onMapRedraw?: () => void;
	onFps?: (fps: number) => void;
	onDragStart?: (wasmId: TWasmId, event: PointerEvent) => Promise<number>;
	onDragMove?: (id: number, event: PointerEvent) => void;
	onDragEnd?: (id: number, event: PointerEvent) => void;
	onBestfit?: (coordinates: Array<[number, number]>) => void;
	onContextLost?: () => void;
	onResize?: (isTouch: boolean) => void;
}

const ALL_OVERLAYS: Array<TWasmOverlay> = ["hillshade", "hillslope", "knLayer", "panoLayer", "touristMode"];
const MAX_PU_EVENTS = 2;
const bestfitDiv = createMapBestfit();
let bestfitStartX = 0;
let bestfitStartY = 0;

export default class Wasm {
	#opts: IWasmOpts;

	#module: typeof Module;

	#mapControl: Module.MapControl;

	#mapContent: Module.ContentService;

	#canvas: HTMLCanvasElement;

	#bindUpdateMapContent: () => void;

	#icons: Map<string, number>;

	#loadData: {
		to: number;
	} & ReturnType<typeof deferPromise>;

	#initProm: ReturnType<typeof deferPromise>;

	#lockMap: boolean;

	#screenshotPromise: null | ReturnType<typeof deferPromise<IScreenshot>>;

	#hoverDoneCb: null | ReturnType<typeof deferPromise>;

	#activePointers: Map<number, IActivePointerItem>;

	#lastHoverRasterLayer: string;

	#lastHoverElementId: number;

	#dragElementId: number;

	#lastHoverPoi: IPoiData;

	#lastPos: IXYPosition;

	#redrawLockData: {
		locked: boolean;
		to: number;
	};

	#textures: WasmTextures;

	#lastIconId: number;

	/** probiha prave drag & drop */
	#dragging: boolean;

	#lineStyles: { [key: string]: Module.LineStyle };

	#stats: Module.Stats;

	/** probiha prave nastavovani rozmeru pro bestfit */
	#fitting: boolean;

	#tiltData: {
		pos: IXYPosition;
		isActive: boolean;
		shift: boolean;
		current: number;
	};

	#lastPUEvents: Array<{ time: number; event: PointerEvent; }>;

	#hoverableMarkers: {[key: IStoreItem["id"]]: WasmMapMarker };

	#lastHoverStoreId: IStoreItem["id"];

	#resizeObserver: ResizeObserver;

	#touchDevice: boolean;

	#longTapData: {
		state: boolean;
		id: number;
	};

	#dpi: {
		textures: number;
		layers: number;
	};

	#filesPath: string;

	#entityChangeCb: () => void;

	constructor(opts?: IWasmOpts) {
		this.#opts = {
			parentElem: null,
			lang: "cs",
			debug: false,
			canvasQuery: "canvas",
			animTo: 400,
			loadTo: 333,
			// pano 100
			resizeTo: 100,
			longTapTo: 750,
			entityChangeTo: 100,
			moveTo: 50,
			maxFps: 0,
			isMobile: false,
			dpi: window.devicePixelRatio,
			position: DEF_POSITION,
			mapStyle: null,
			urls: {
				poiAdmin: "https://vectmap.mapy.cz/rpc/",
				styles: `/wasm/styles-${WASM_VERSION.version}/`,
			},
			clickDistanceMouse: 3,
			clickDistanceTouch: 20,
			dblClickTo: 350,
			enableTilt: false,
			release: false,
			onMapRedraw: () => {},
			onFps: () => {},
			onDblClick: () => {},
			onContextLost: () => {},
			onResize: () => {},
			...opts,
		};
		this.#mapControl = null;
		this.#canvas = document.querySelector(this.#opts.canvasQuery);
		this.#bindUpdateMapContent = this.#updateMapContent.bind(this);
		// tady je to pouze ukazatel, nevraci se to a nenastavuje se to
		this.#icons = new Map<string, number>();
		this.#lastIconId = 0;
		this.#dragging = false;
		this.#loadData = {
			to: 0,
			...deferPromise(),
		};
		this.#initProm = deferPromise();
		this.#lockMap = false;
		this.#screenshotPromise = null;
		this.#hoverDoneCb = null;
		this.#activePointers = new Map();
		this.#lastHoverRasterLayer = "";
		this.#lastHoverPoi = null;
		this.#lastHoverElementId = 0;
		this.#lastPos = {
			x: 0,
			y: 0,
		};
		this.#redrawLockData = {
			locked: false,
			to: 0,
		};

		const dpi = getCorrectDpi(this.#opts.dpi);

		this.#dpi = {
			textures: dpi,
			layers: dpi,
		};
		this.#textures = new WasmTextures(this.#dpi.textures);
		this.#lineStyles = {};
		this.#stats = {
			fps: 0,
			drawBuffersSize: 0,
			drawCnt: 0,
			frameIdx: 0,
			memUsedSize: 0,
			roCnt: 0,
			texMemSize: 0,
			triCnt: 0,
		};
		this.#tiltData = {
			isActive: false,
			pos: null,
			shift: false,
			current: TILT_DATA.angles[0],
		};
		this.#lastPUEvents = [];
		this.#hoverableMarkers = {};
		this.#lastHoverStoreId = 0;
		this.#resizeObserver = null;
		this.#touchDevice = isTouchDevice();
		this.#longTapData = {
			id: 0,
			state: false,
		};
		this.#filesPath = this.#opts.release
			? `/wasm/release-${WASM_VERSION.version}/`
			: `/wasm/develop-${WASM_VERSION.version}/`;
		this.#entityChangeCb = debounce(() => {
			this.#mapControl?.requestFrame();
		}, this.#opts.entityChangeTo);

		this.#load();
	}

	destroy() {
		this.#opts.enableTilt && window.removeEventListener("keydown", this);
		this.#mapControl.disconnect();
		this.#resizeObserver?.disconnect();
	}

	removeHoverId(id: IStoreItem["id"]) {
		if (id in this.#hoverableMarkers) {
			delete this.#hoverableMarkers[id];
		}
	}

	entityChange() {
		this.#entityChangeCb();
	}

	handleEvent(event: KeyboardEvent) {
		switch (event.type) {
			case "keydown": {
				if (event.key === "Shift" || event.shiftKey) {
					this.#tiltData.shift = true;
					window.addEventListener("keyup", this);
				}
				break;
			}

			case "keyup":
				this.#tiltData.shift = false;
				window.removeEventListener("keyup", this);
				break;

			default:
		}
	}

	unhoverMarker() {
		this.#hoverableMarkers[this.#lastHoverStoreId]?.setHover(false);
	}

	#getModuleConf() {
		return {
			printErr: (...args: any) => {
				if (args.length > 1) {
					// eslint-disable-next-line no-console
					console.log(args.join(" "));
				} else {
					// eslint-disable-next-line no-console
					console.log(args[0]);
				}
			},
			totalDependencies: 0,
			monitorRunDependencies: (left: number) => {
				if (this.#loadData.to) {
					clearTimeout(this.#loadData.to);
					this.#loadData.to = 0;
				}

				this.#loadData.to = setTimeout(() => {
					if (left === 0) {
						this.#loadData.resolve({});
					}
				}, this.#opts.loadTo) as unknown as number;
			},
			locateFile: (file: string) => `${this.#filesPath}${file}`,
		};
	}

	async #load() {
		/* eslint-disable-next-line */
		this.#opts.debug && console.log(`Wasm load version = ${WASM_VERSION.version}, date = ${WASM_VERSION.date}`);

		try {
			const wasmEnabled = await isWasmEnabled();
			const jsFileName = wasmEnabled
				? "lib-maps.js"
				: "lib-maps-js.js";
			const libSznMaps = (await import(/* webpackIgnore: true */ `${this.#filesPath}${jsFileName}`)).default;
			const loadProm = libSznMaps(this.#getModuleConf());
			const [module] = await Promise.all([
				loadProm,
				this.#textures.load(),
			]);

			this.#setModule(module as typeof Module);
		} catch (exc) {
			/* eslint-disable-next-line */
			console.log(exc);
			this.#initProm.reject(exc);
		}
	}

	#setModule(module: typeof Module) {
		this.#module = module;

		module.setLogLevel(this.#opts.debug ? module.LogLevel.Debug : module.LogLevel.Warning);

		// create poi admin cache, wich resolves poi iconst for map / page
		const poiAdmin = new module.PoiAdminCache(this.#opts.urls.poiAdmin);

		// create map
		const mapControl = new module.MapControl(this.#opts.isMobile) as Module.MapControl;

		// souradnice
		mapControl.setPositionWgs({
			x: this.#opts.position.lon,
			y: this.#opts.position.lat,
		});
		mapControl.setZoom(this.#opts.position.zoom);

		if (this.#opts.urlReplacement) {
			const { fromUrl, toUrl, authServerUrl } = this.#opts.urlReplacement;
			const mapHostsSetup = new module.MapHostsSetup();

			if (authServerUrl) {
				mapHostsSetup.setAuthorizationServer(authServerUrl);
			}

			mapHostsSetup.setUrlReplacement(fromUrl, toUrl, !!authServerUrl);
			mapControl.setMapHostsSetup(mapHostsSetup);
		}

		this.#mapControl = mapControl;

		if (!this.#opts.maxFps) {
			this.#mapControl.setTargetFps(this.#opts.maxFps);
		}

		const moveCb = debounce(() => {
			if (this.#redrawLockData.locked) {
				return;
			}

			this.#opts.onMapRedraw();
		}, this.#opts.moveTo);

		mapControl.setOnMapViewChanged(() => {
			moveCb();
		});

		// setup poi icon resolver used by map - poiAdmin class
		mapControl.setCustomPoiIconResolver(poiAdmin);

		// initialize map cotent service
		this.#initMapContentService(mapControl.getContentService());

		// load styles from server path & show them in console, select default stype set after load
		mapControl.loadStyles(this.#opts.urls.styles, DEF_DPIS, this.#dpi.layers).then(() => {
			// mame styly?
			if (this.#opts.mapStyle) {
				this.setMapstyle(this.#opts.mapStyle);
			} else {
				this.#mapControl.setDefaultStyleSet();
			}
		});

		// connect map to canvas & update size
		mapControl.connectToCanvas(this.#opts.canvasQuery);

		this.#canvas.tabIndex = -1;
		this.#canvas.style.width = `100%`;
		this.#canvas.style.height = `100%`;

		if (this.#touchDevice) {
			this.#canvas.addEventListener("touchstart", event => event.preventDefault());
		} else {
			// send mouse events to map
			this.#canvas.addEventListener("contextmenu", event => {
				event.preventDefault();
				this.#mapControl.onClick(event.clientX, event.clientY, { action: "contextmenu", event: event });
			});
		}

		this.#canvas.addEventListener("webglcontextlost", () => {
			this.#opts.onContextLost();
		});

		// update size of map now
		this.#updateMapControlSize();

		// start render loop
		window.requestAnimationFrame(this.#bindUpdateMapContent);

		mapControl.setOnRenderStats(stats => {
			this.#stats = stats;
			this.#opts.onFps(stats.fps);
		});

		this.#initMoveControls();

		this.#opts.enableTilt && this.#initKeyboard();

		poiAdmin.delete();

		const resizeCb = debounce(() => {
			this.syncPort();
			this.#opts.onResize(this.#touchDevice);
		}, this.#opts.resizeTo);

		this.#resizeObserver = new ResizeObserver(() => {
			resizeCb();
		});

		this.#resizeObserver.observe(this.#opts.parentElem);

		this.#initProm.resolve({});
	}

	get loadProm() {
		return Promise.all([
			this.#loadData.promise,
			this.#initProm.promise,
		]);
	}

	set lockMap(lockMap: boolean) {
		this.#lockMap = lockMap;
	}

	syncPort() {
		this.#updateMapControlSize();
	}

	createMarker(markerData: IWasmStoreMarker) {
		// specialni typ
		if (markerData.type === "position-share" || markerData.type === "position-share-arrow") {
			return new WasmMapMarkerSharedPosition(this, markerData);
		}

		const newMarker = new WasmMapMarker(this, markerData);

		// ulozime
		if (markerData.hover) {
			this.#hoverableMarkers[markerData.id] = newMarker;
		}

		return newMarker;
	}

	createGeometry(geomData: IWasmStoreGeometry) {
		return new WasmMapGeometry(this, geomData);
	}

	// ziskani stylu - pokud neexistuje, vytvorime jej
	createGeometryStyle(styleHash: string) {
		if (!(styleHash in this.#lineStyles)) {
			const styleData = parseGeometryStyleHash(styleHash);
			let lineCap: Module.LineCapType = this.#module.LineCapType.Butt;
			let linJoin: Module.LineJointType = this.#module.LineJointType.Bevel;

			switch (styleData.lineCap) {
				case "butt":
					lineCap = this.#module.LineCapType.Butt;
					break;

				case "round":
					lineCap = this.#module.LineCapType.Round;
					break;

				case "square":
					lineCap = this.#module.LineCapType.Square;
					break;

				default:
			}

			switch (styleData.lineJoin) {
				case "bevel":
					linJoin = this.#module.LineJointType.Bevel;
					break;

				case "round":
					linJoin = this.#module.LineJointType.Round;
					break;

				case "miter":
					linJoin = this.#module.LineJointType.Miter;
					break;

				default:
			}

			const styleInst = new this.#module.LineStyle(
				styleData.width,
				styleData.color,
				styleData.edgeWidth,
				styleData.edgeColor,
				styleData.keepExactWidth,
				lineCap,
				linJoin,
				styleData.dashed,
			);

			this.#lineStyles[styleHash] = styleInst;
		}

		return this.#lineStyles[styleHash];
	}

	async setMapstyle({
		base,
		overlays = [],
		variant = "",
		ophotoTimeline = "",
		trafficOverlay = false,
		dpiScale,
	}: IMapStyle) {
		const wasmName = LAYER_MAPPING[base];
		const styleSet = new this.#module.StyleSetCfg(wasmName, variant);
		const poiSets: Array<string> = [];
		const debug: any = {
			base,
			wasmName,
			variant,
			attrs: {},
		};

		// set styleset attributes values
		for (const overlay of ALL_OVERLAYS) {
			const value = overlays.includes(overlay) ? "true" : "false";

			styleSet.setAttribute(overlay, value);
			debug.attrs[overlay] = value;
		}

		// pro katastr ridime poie v lookupu
		if (overlays.includes("knLayer")) {
			poiSets.push("cadastral");
			poiSets.push(LAYER_MAPPING_NUMB[base].toString());
		}

		if (trafficOverlay) {
			poiSets.push(LAYER_MAPPING_NUMB.dopravni.toString());
			poiSets.push(LAYER_MAPPING_NUMB[base].toString());
		}

		if (poiSets.length > 0) {
			const value = poiSets.join(",");

			styleSet.setAttribute("poiSets", value);
			debug.attrs.poiSets = value;
		}

		if (typeof dpiScale === "number") {
			const value = dpiScale.toFixed(1);

			styleSet.setAttribute("dpiScale", dpiScale.toFixed(1));
			debug.attrs.dpiScale = value;
		}

		// modifikator ophoto
		if (ophotoTimeline) {
			const src = OPHOTO_YEAR_SRC_MAPPING[ophotoTimeline];

			if (src) {
				styleSet.setAttribute("ophotoYearSrc", src);
				debug.attrs.ophotoYearSrc = src;
			}
		}

		if (!CZECH_TILES.includes(this.#opts.lang)) {
			styleSet.setAttribute("lang", "en");
		}

		// set styleset config to map
		await this.#mapControl.setStyleSet(styleSet);

		/* eslint-disable-next-line */
		this.#opts.debug && console.log(["wasm map style", debug]);

		// delete after use
		styleSet.delete();
	}

	loseContext() {
		if (this.#module?.ctx) {
			this.#module.ctx.getExtension("WEBGL_lose_context").loseContext();
		}
	}

	getScreenshot() {
		this.#screenshotPromise = deferPromise<IScreenshot>();
		this.#mapControl.requestFrame();

		return this.#screenshotPromise.promise;
	}

	async getDebugScreenshot() {
		const styleSet = new this.#module.StyleSetCfg("", "");

		await this.#mapControl.setStyleSet(styleSet);

		this.#screenshotPromise = deferPromise<IScreenshot>();
		this.#mapControl.requestFrame();

		return this.#screenshotPromise.promise;
	}

	async setCenterZoom(wgsX: number, wgsY: number, zoom?: number) {
		this.#redrawLockData.locked = true;

		if (zoom) {
			await this.#mapControl.setMapPositionWgsAndZoom({
				x: wgsX,
				y: wgsY,
			}, zoom);
		} else {
			this.#mapControl.setPositionWgs({
				x: wgsX,
				y: wgsY,
			});
		}

		this.#redrawLockData.locked = false;
	}

	setCenterZoomAnim(wgsX: number, wgsY: number, zoom: number, animTo?: number) {
		this.#redrawLockData.locked = true;

		const timeout = animTo ?? this.#opts.animTo;
		const setupData = this.#mapContent.getMapViewSetupToShowWgsPosAndZoom({
			x: wgsX,
			y: wgsY,
		}, zoom);

		this.mapControl.animToMapView(setupData.mapViewSetup, timeout, this.module.EasingType.Linear);
		this.#unlockRedraw(timeout);
	}

	/** Pouzit pouze na pixely viditelne na obrazovce, ne mimo ni!!! */
	async zoomToPixel(pixel: IXYPosition, zoom: number, animTo?: number) {
		this.#redrawLockData.locked = true;

		const timeout = animTo ?? this.#opts.animTo;
		const setupData = await this.mapControl.createMapViewSetupToZoomAt(pixel, zoom);

		this.mapControl.animToMapView(setupData, timeout, this.module.EasingType.Linear);
		this.#unlockRedraw(timeout);
	}

	async getCenterZoom() {
		const [coords, zoom] = await Promise.all([
			this.#mapControl.getPositionWgs(),
			this.#mapControl.getZoom(),
		]);

		return {
			lon: coords.x,
			lat: coords.y,
			zoom,
		};
	}

	getZoom() {
		return this.#mapControl.getZoom();
	}

	coordsFromEvent(event: PointerEvent): Promise<IXYPosition> {
		const { clientX, clientY } = event;

		return this.coordsFromScreenPosition(clientX, clientY);
	}

	async coordsFromScreenPosition(clientX: number, clientY: number): Promise<IXYPosition> {
		const pos = await this.#mapControl.screenPosToMapPos({
			x: clientX,
			y: clientY,
		});
		const wgs = this.#module.GeoMath.mercatorToWGS(pos);

		return wgs;
	}

	coordsToPixel(wgs84: IXYPosition) {
		const merc = this.#module.GeoMath.wgsToMercator(wgs84);

		return this.#mapControl.mapPosToScreenPos(merc);
	}

	redraw() {
		this.#mapControl?.requestFrame();
	}

	get textures() {
		return this.#textures;
	}

	get icons() {
		return this.#icons;
	}

	get lineStyles() {
		return this.#lineStyles;
	}

	get module() {
		return this.#module;
	}

	get mapControl() {
		return this.#mapControl;
	}

	get mapContent() {
		return this.#mapContent;
	}

	get stats() {
		const output: any = {
			...this.#stats,
		};

		output.drawBuffersSize = formatSize(output.drawBuffersSize);
		output.texMemSize = formatSize(output.texMemSize);
		output.memUsedSize = formatSize(output.memUsedSize);

		return output;
	}

	async getViewport(): Promise<null | { lb: ILonLatPosition; rt: ILonLatPosition; }> {
		const width = this.#canvas.width / this.#dpi.textures;
		const height = this.#canvas.height / this.#dpi.textures;

		if (!this.#mapControl) {
			return null;
		}

		const [rtPosMerc, lbPosMerc] = await Promise.all([
			this.#mapControl.screenPosToMapPos({
				x: width,
				y: 0,
			}),
			this.#mapControl.screenPosToMapPos({
				x: 0,
				y: height,
			}),
		]);

		const rtWgs = this.#module.GeoMath.mercatorToWGS(rtPosMerc);
		const lbWgs = this.#module.GeoMath.mercatorToWGS(lbPosMerc);

		return {
			lb: {
				lon: lbWgs.x,
				lat: lbWgs.y,
			},
			rt: {
				lon: rtWgs.x,
				lat: rtWgs.y,
			},
		};
	}

	getTextureData(nameId: string): {
		id: number;
		exists: boolean;
	} {
		const id = this.#icons.get(nameId);

		if (id) {
			return {
				id,
				exists: true,
			};
		}

		this.#lastIconId++;
		this.#icons.set(nameId, this.#lastIconId);

		return {
			id: this.#lastIconId,
			exists: false,
		};
	}

	#initMapContentService(contentService: Module.ContentService) {
		// setup global variable
		this.#mapContent = contentService;

		// textury
		registerTextures(this, this.#dpi.textures);
		// provider - reseni url pro textury
		bindWasmTextureUrlProvider(this);

		contentService.setOnClickCallback(async (clickData, actionUserData) => {
			const poisData: Array<IPoiData> = [];
			let rasterLayer = "";

			// vyplnime dat o POIich
			if (clickData?.DataType === this.#module.ActionDataType.Poi) {
				for (let ind = 0; ind < clickData.Pois.size(); ind++) {
					poisData.push(poiInfoToJson(clickData.Pois.get(ind), this));
				}
			}

			if (clickData?.DataType === this.#module.ActionDataType.RasterLayerHit) {
				rasterLayer = clickData.rasterHitResult;
			}

			// kontextove menu
			if (actionUserData?.action === "contextmenu") {
				this.#opts.onContextMenu(actionUserData.event, poisData, clickData?.ElementId);
			} else if (actionUserData?.action === "click") {
				const [wgs, contact] = await Promise.all([
					this.coordsFromScreenPosition(actionUserData.event.clientX, actionUserData?.event.clientY),
					clickData?.ContactPos
						? this.#mapControl.mapPosToScreenPos(clickData?.ContactPos)
						: Promise.resolve({ x: 0, y: 0 }),
				]);

				this.#opts.onMapClick?.({
					pois: poisData,
					wasmId: clickData?.ElementId,
					longitude: wgs?.x ?? 0,
					latitude: wgs?.y ?? 0,
					clientX: actionUserData.event.clientX ?? 0,
					clientY: actionUserData.event.clientY ?? 0,
					contactX: contact.x,
					contactY: contact.y,
					ctrlKey: actionUserData.event.ctrlKey,
					shiftKey: actionUserData.event.shiftKey,
					altKey: actionUserData.event.altKey,
					metaKey: actionUserData.event.metaKey,
					button: actionUserData.event.button,
					pointerType: actionUserData.event.pointerType,
					rasterLayer,
				});
			}

			// smazeme data -> kvuli infu do konzole
			clickData?.delete();
		});

		contentService.setOnHoverCallback(async hoverData => {
			const newElementId = hoverData?.ElementId || 0;
			let newPoi: IPoiData = null;
			const [wgs, contact] = await Promise.all([
				this.coordsFromScreenPosition(this.#lastPos.x, this.#lastPos.y),
				hoverData?.ContactPos
					? this.#mapControl.mapPosToScreenPos(hoverData?.ContactPos)
					: Promise.resolve({ x: 0, y: 0 }),
			]);
			const commonData: THoverData = {
				longitude: wgs?.x || 0,
				latitude: wgs?.y || 0,
				clientX: this.#lastPos.x,
				clientY: this.#lastPos.y,
				contactX: contact.x,
				contactY: contact.y,
				actionUserData: null,
			};
			let rasterLayer = "";

			if (hoverData?.DataType === this.#module.ActionDataType.Poi && hoverData?.Pois.size() > 0) {
				newPoi = poiInfoToJson(hoverData.Pois.get(0), this);
			}

			if (hoverData?.DataType === this.#module.ActionDataType.RasterLayerHit) {
				rasterLayer = hoverData.rasterHitResult;
			}

			if (hoverData?.DataType === this.#module.ActionDataType.MapElement && hoverData?.UserData) {
				commonData.actionUserData = hoverData.UserData;
			}

			// smazeme data -> kvuli infu do konzole
			hoverData?.delete();

			this.#hover(newElementId, commonData, rasterLayer, newPoi);

			if (this.#hoverDoneCb) {
				this.#hoverDoneCb.resolve({});
			}
		});
	}

	#hover(newElementIdArg: number, commonData: THoverData, rasterLayer: string, newPoi: IPoiData) {
		let newElementId = newElementIdArg;
		let newStoreId = 0;
		let hoverMarker: WasmMapMarker = null;

		if (newElementId) {
			const curHoverMarkers = Object.entries(this.#hoverableMarkers);

			for (const hoverMarkerEntry of curHoverMarkers) {
				if (hoverMarkerEntry[1].wasmIds.includes(newElementId)) {
					hoverMarker = hoverMarkerEntry[1];
					newStoreId = parseFloat(hoverMarkerEntry[0]);
					break;
				}
			}
		}

		if (hoverMarker && (!this.#lastHoverStoreId || this.#lastHoverStoreId !== newStoreId)) {
			newElementId = hoverMarker.setHover(true);
		}

		if (this.#lastHoverStoreId && this.#hoverableMarkers[this.#lastHoverStoreId] && this.#lastHoverStoreId !== newStoreId) {
			this.unhoverMarker();
		}

		this.#lastHoverStoreId = newStoreId;

		// zadna zmena elementu a element je nastaveny - pohybujeme se porad po tom samem markeru
		if (this.#lastHoverElementId === newElementId && newElementId) {
			this.#opts.onHover({
				type: "move",
				wasmId: newElementId,
				poi: null,
				rasterLayer: "",
				...commonData,
			});

			return;
		}

		// zadna zmena POie a POI je nastaveny - pohybujeme se porad po tom samem POIi
		if (this.#lastHoverPoi?.source === newPoi?.source && this.#lastHoverPoi?.id === newPoi?.id && newPoi) {
			this.#opts.onHover({
				type: "move",
				wasmId: 0,
				poi: newPoi,
				rasterLayer: "",
				...commonData,
			});

			return;
		}

		// zadna zmena POie a POI je nastaveny - pohybujeme se porad po tom samem POIi
		if (this.#lastHoverRasterLayer === rasterLayer && rasterLayer) {
			this.#opts.onHover({
				type: "move",
				wasmId: 0,
				poi: null,
				rasterLayer,
				...commonData,
			});

			return;
		}

		// nejdriv udelame oba potencialni leave
		this.#lastHoverElementId && this.#opts.onHover({
			type: "leave",
			wasmId: this.#lastHoverElementId,
			poi: null,
			rasterLayer: "",
			...commonData,
		});
		this.#lastHoverPoi && this.#opts.onHover({
			type: "leave",
			wasmId: 0,
			poi: this.#lastHoverPoi,
			rasterLayer: "",
			...commonData,
		});
		this.#lastHoverRasterLayer && this.#opts.onHover({
			type: "leave",
			wasmId: 0,
			poi: null,
			rasterLayer: this.#lastHoverRasterLayer,
			...commonData,
		});

		// a pak oba potencialni entery - jinak by se nam michalo poradi enter/leave a to by byl problem napriklad pro zmeny kurzoru
		newElementId && this.#opts.onHover({
			type: "enter",
			wasmId: newElementId,
			poi: null,
			rasterLayer: "",
			...commonData,
		});
		newPoi && this.#opts.onHover({
			type: "enter",
			wasmId: 0,
			poi: newPoi,
			rasterLayer: "",
			...commonData,
		});
		rasterLayer && this.#opts.onHover({
			type: "enter",
			wasmId: 0,
			poi: null,
			rasterLayer,
			...commonData,
		});

		// zapamatujeme si nova data
		this.#lastHoverElementId = newElementId;
		this.#lastHoverPoi = newPoi;
		this.#lastHoverRasterLayer = rasterLayer;
	}

	#updateMapControlSize() {
		const dpiScale = this.#dpi.textures;
		const rect = this.#opts.parentElem.getBoundingClientRect();
		const width = rect.width;
		const height = rect.height;

		this.#canvas.width = alignPixel(width * dpiScale);
		this.#canvas.height = alignPixel(height * dpiScale);

		// eslint-disable-next-line no-console
		this.#opts.debug && console.log(`Canvas width ${this.#canvas.width} height ${this.#canvas.height}`);

		if (this.#mapControl) {
			this.#mapControl.setHostSize(width, height);
			this.#mapControl.setRenderResolution(this.#canvas.width, this.#canvas.height);
			window.requestAnimationFrame(this.#bindUpdateMapContent);
		}
	}

	#updateMapContent(timestamp: number) {
		if (this.#mapControl) {
			this.#mapControl.update(timestamp);

			if (this.#screenshotPromise) {
				const imgSrcData = this.#canvas.toDataURL();

				this.#screenshotPromise.resolve({
					data: imgSrcData,
					width: this.#canvas.width / this.#dpi.textures,
					height: this.#canvas.height / this.#dpi.textures,
				});
				this.#screenshotPromise = null;
			}

			window.requestAnimationFrame(this.#bindUpdateMapContent);
		}
	}

	async #eventDown(event: PointerEvent) {
		// pokud tohle neudelame, tak bude nastavat blur na inputech, nez se dozvi, ze se pro ne kliklo do mapy
		event.preventDefault();
		this.#lastPos.x = event.clientX;
		this.#lastPos.y = event.clientY;

		if (this.#lockMap || !this.#mapControl) {
			return;
		}

		// nakloneni
		if (this.#tiltData.shift && this.#activePointers.size === 0) {
			this.#tiltData.isActive = true;
			this.#tiltData.pos = {
				x: event.clientX,
				y: event.clientY,
			};
			this.#activePointers.set(event.pointerId, {
				event,
				type: "tilt",
			});
		// klik
		} else if (event.pointerType !== "mouse" || event.button === 0) {
			const eventItem: IActivePointerItem = {
				event,
				type: "down",
			};

			if (this.#touchDevice && this.#activePointers.size === 0) {
				// send hover to map
				this.#hoverDoneCb = deferPromise();

				await Promise.all([
					this.#mapControl.onHover(event.clientX, event.clientY),
					this.#hoverDoneCb.promise,
				]);

				this.#hoverDoneCb = null;
			}

			this.#dragElementId = this.#lastHoverElementId
				? await this.#opts.onDragStart?.(this.#lastHoverElementId, event) ?? 0
				: 0;

			// s CTRL nebo META zaciname delat obdelnik pro bestfit
			if ((event.ctrlKey || event.metaKey) && this.#activePointers.size === 0) {
				this.#fitting = true;
				bestfitStartX = event.clientX;
				bestfitStartY = event.clientY;
				bestfitDiv.style.left = `${bestfitStartX}px`;
				bestfitDiv.style.top = `${bestfitStartY}px`;
				bestfitDiv.style.width = `0px`;
				bestfitDiv.style.height = `0px`;
				document.body.appendChild(bestfitDiv);
				eventItem.type = "fit";
			// pokud to dava smysl, zaciname s drag & drop
			} else if (this.#activePointers.size === 0 && this.#dragElementId) {
				this.#dragging = true;
				this.#longTap(event);
				eventItem.type = "dragging";
			// jinak posleme data do mapy a ta si udela vlastni gesto, pravdepodobne pohyb mapou
			} else {
				this.#mapControl.onPointerDown(event.pointerId, event.clientX, event.clientY, {
					event: {
						ctrlKey: event.ctrlKey,
						shiftKey: event.shiftKey,
						altKey: event.altKey,
						metaKey: event.metaKey,
						button: event.button,
						pointerType: event.pointerType,
						clientX: event.clientX,
						clientY: event.clientY,
					},
				});
				this.#longTap(event);
				eventItem.type = "down";
			}

			this.#activePointers.set(event.pointerId, eventItem);
		}
	}

	#longTap(event: PointerEvent) {
		if (this.#touchDevice) {
			this.#longTapData.id = setTimeout(() => {
				this.#longTapData.id = null;

				const dist = Math.sqrt(Math.pow(event.clientX - this.#lastPos.x, 2) + Math.pow(event.clientY - this.#lastPos.y, 2));

				if (this.#activePointers.size === 1 && this.#checkDistance(dist)) {
					this.#dragging = false;
					this.#longTapData.state = true;
					this.#opts.onContextMenu(event, this.#lastHoverPoi ? [this.#lastHoverPoi] : [], this.#lastHoverElementId);

					this.#mapControl.onPointerUp(event.pointerId, event.clientX, event.clientY);
					this.#activePointers.delete(event.pointerId);
				}
			}, this.#opts.longTapTo) as unknown as number;
		}
	}

	#eventMove(event: PointerEvent) {
		this.#lastPos.x = event.clientX;
		this.#lastPos.y = event.clientY;

		if (this.#lockMap || !this.#mapControl || this.#longTapData.state) {
			return;
		}

		// nakloneni
		if (this.#tiltData.isActive && this.#activePointers.has(event.pointerId)) {
			const dist = Math.sqrt(Math.pow(event.clientX - this.#tiltData.pos.x, 2) + Math.pow(event.clientY - this.#tiltData.pos.y, 2));
			const direction = event.clientY > this.#tiltData.pos.y
				? 1
				: -1;
			const percent = Math.min(dist / TILT_DATA.distance, 1);
			const anglesDistances = TILT_DATA.angles[1] - TILT_DATA.angles[0];
			const diffAngle = anglesDistances * percent;
			const newAngleUnc = this.#tiltData.current - (direction * diffAngle);
			const newAngle = Math.min(Math.max(newAngleUnc, TILT_DATA.angles[0]), TILT_DATA.angles[1]);

			this.#tiltData.current = newAngle;
			this.#tiltData.pos.x = event.clientX;
			this.#tiltData.pos.y = event.clientY;
			this.#mapControl.setTiltAngle(newAngle);
		// zrovna tahame obdelnik pro bestfit
		} else if (this.#fitting) {
			// updatujeme obdelnik pro bestfit
			bestfitDiv.style.left = `${Math.max(Math.min(bestfitStartX, event.clientX), 0)}px`;
			bestfitDiv.style.top = `${Math.max(Math.min(bestfitStartY, event.clientY), 0)}px`;
			bestfitDiv.style.width = `${Math.abs(Math.min(Math.max(event.clientX, 0), this.#canvas.offsetWidth) - bestfitStartX)}px`;
			bestfitDiv.style.height = `${Math.abs(Math.min(Math.max(event.clientY, 0), this.#canvas.offsetHeight) - bestfitStartY)}px`;
		} else if (this.#dragging) {
			// drag on element
			this.#opts.onDragMove?.(this.#dragElementId, event);
		} else if (this.#activePointers.has(event.pointerId)) {
			this.#mapControl.onPointerMove(event.pointerId, event.clientX, event.clientY);
		} else if (this.#activePointers.size === 0 && event.pointerType === "mouse" && event.target === this.#canvas) {
			// send hover to map
			this.#mapControl?.onHover(event.clientX, event.clientY);
		}
	}

	#eventUp(event: PointerEvent) {
		if (this.#lockMap || !this.#mapControl) {
			return;
		}

		const wasLongTap = this.#longTapData.state;

		this.#longTapData.state = false;

		if (this.#longTapData.id) {
			clearTimeout(this.#longTapData.id);
			this.#longTapData.id = null;
		}

		// bylo nakloneni nebo long tap
		if (this.#tiltData.isActive || wasLongTap) {
			this.#tiltData.isActive = false;
		// presun
		} else if (this.#dragging) {
			const dragId = this.#dragElementId;

			this.#dragging = false;
			this.#dragElementId = 0;
			// drag end on element
			this.#opts.onDragEnd?.(dragId, event);
		} else if (this.#activePointers.has(event.pointerId)) {
			const pointersSize = this.#activePointers.size;

			if (pointersSize === 1) {
				const origEvent = this.#activePointers.get(event.pointerId).event;
				const dist = Math.sqrt(Math.pow(event.clientX - origEvent.clientX, 2) + Math.pow(event.clientY - origEvent.clientY, 2));

				if (this.#checkDistance(dist)) {
					this.#mapControl.onPointerCancel(event.pointerId);
					this.#mapControl.onClick(event.clientX, event.clientY, { action: "click", event: event });
					this.unhoverMarker();
				} else if (this.#fitting) {
					this.#bestFit();
				}

				this.#fitting = false;
				bestfitDiv.remove();
				this.#lastPUEvents.push({ time: Date.now(), event });

				if (this.#lastPUEvents.length > MAX_PU_EVENTS) {
					this.#lastPUEvents.shift();
				}

				if (this.#lastPUEvents.length === MAX_PU_EVENTS) {
					const cur = this.#lastPUEvents[1];
					const prev = this.#lastPUEvents[0];
					const diff = cur.time - prev.time;

					if (diff <= this.#opts.dblClickTo && !this.#lastHoverElementId && !this.#lastHoverPoi) {
						const distDblClck = Math.sqrt(Math.pow(cur.event.clientX - prev.event.clientX, 2) + Math.pow(cur.event.clientY - prev.event.clientY, 2));

						if (this.#checkDistance(distDblClck)) {
							this.#opts.onDblClick(event);
						}
					}
				}
			}
		}

		if (this.#touchDevice && this.#activePointers.size === 1) {
			// send hover to map
			this.#mapControl.onHover(event.clientX, event.clientY);
		}

		const pointerData = this.#activePointers.get(event.pointerId);

		if (pointerData?.type === "down") {
			this.#mapControl.onPointerUp(event.pointerId, event.clientX, event.clientY);
		}

		this.#activePointers.delete(event.pointerId);
	}

	#eventCancel(event: PointerEvent) {
		if (this.#mapControl && !this.#lockMap) {
			this.#mapControl.onPointerCancel(event.pointerId);
		}

		this.#activePointers.delete(event.pointerId);
	}

	async #bestFit() {
		const [leftTop, rightBottom] = await Promise.all([
			this.coordsFromScreenPosition(parseInt(bestfitDiv.style.left, 10), parseInt(bestfitDiv.style.top, 10)),
			this.coordsFromScreenPosition(parseInt(bestfitDiv.style.left, 10) + parseInt(bestfitDiv.style.width, 10), parseInt(bestfitDiv.style.top, 10) + parseInt(bestfitDiv.style.height, 10)),
		]);

		this.#opts.onBestfit?.([[leftTop.x, leftTop.y], [rightBottom.x, rightBottom.y]]);
	}

	#initMoveControls() {
		const elem = this.#opts.parentElem;

		// web
		elem.addEventListener("pointerdown", event => {
			this.#eventDown(event);
		});
		window.addEventListener("pointermove", event => {
			this.#eventMove(event);
		});
		window.addEventListener("pointerup", event => {
			this.#eventUp(event);
		});
		window.addEventListener("pointercancel", event => {
			this.#eventCancel(event);
		});

		/* eslint-disable-next-line */
		new WasmScroll(this, elem);
	}

	#initKeyboard() {
		window.addEventListener("keydown", this);
	}

	#checkDistance(distance: number) {
		const threshold = this.#touchDevice
			? this.#opts.clickDistanceTouch
			: this.#opts.clickDistanceMouse;

		return distance <= threshold;
	}

	#unlockRedraw(to: number) {
		if (this.#redrawLockData.to) {
			clearTimeout(this.#redrawLockData.to);
			this.#redrawLockData.to = 0;
		}

		this.#redrawLockData.to = setTimeout(() => {
			this.#redrawLockData.to = 0;
			this.#redrawLockData.locked = false;
			this.#opts.onMapRedraw();
		}, to) as unknown as number;
	}
}
